import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Check, CircleAlert, LoaderCircle, LogOut, Mic, Radio } from 'lucide-react'
import { Avatar, Button } from '@/components/play'
import { personToneKey } from '@/lib/profile'
import type { ChatCard, ChatPerson } from '@/lib/watch/invitesApi'
import { initialsSource, nameOrHandle, profileAvatarUrl } from '@/lib/watch/rules'
import { cn } from '@/lib/utils'
import './invites.css'

/**
 * Where an invite stands, as the page knows it. `false`: open (the invited viewer gets Join stage and Not now).
 * `'waiting'`: on its way (the invited viewer is checking their camera, or a host waits for their guest). `'joined'` and
 * `'declined'`: answered. `true`: answered, with nothing to say (the viewer is on stage already, say).
 */
export type StageCardAnswer = boolean | 'waiting' | 'joined' | 'declined'

export interface StageCardProps {
  /** The card from the chat (`ChatMessagePayload` with a card kind): who posted it, about whom, and when. */
  card: Pick<ChatCard, 'kind' | 'user' | 'subject' | 'created_at'>
  /** The signed-in viewer's user id: an invite for them reads "invited you" and offers Join stage. */
  viewerId?: string | null
  /** Join stage: the page opens the camera check. */
  onJoin?: () => void
  /**
   * Not now: the page declines (`declineInvite`). Return the promise to show progress; once it resolves the card reads
   * as declined, and a rejection keeps the buttons with a line to try again.
   */
  onDecline?: () => void | Promise<unknown>
  /**
   * A word beside Join stage on your open invite, for when Not now would change nothing (leave `onDecline` out), such
   * as "On stage in another tab".
   */
  note?: string
  answered?: StageCardAnswer
  /** The row's element: a list item in the chat's list, or a div elsewhere. */
  as?: 'li' | 'div'
  className?: string
}

/** Who a card names, when their name and handle are both gone. */
const SOMEONE = 'Someone'

const nameOf = (person: ChatPerson) => nameOrHandle(person.name, person.handle, SOMEONE)

function PersonAvatar({ person }: { person: ChatPerson }) {
  return (
    <Avatar
      name={initialsSource(person.name, person.handle)}
      src={profileAvatarUrl(person.avatar_url)}
      size="xs"
      tone="auto"
      toneKey={personToneKey(person)}
    />
  )
}

/**
 * One chat card, as the prototype's `.sys` rows (docs/features/prototypes/watch-experience): an avatar pair and one
 * line. An invite pairs the host with the person invited, on the selection tint: "Shane invited Sam on stage". For the
 * person invited it reads "Shane invited you on stage" on yellow, with **Join stage** and **Not now** until answered,
 * then how it stands, on the invite's tint as everyone else sees it (`data-answered`). Joined and left pair the guest with what happened: "Sam joined the stage", "Sam left the stage".
 * Names render in isolates, never as HTML.
 */
export function StageCard({ card, viewerId = null, onJoin, onDecline, note, answered = false, as: Row = 'li', className }: StageCardProps) {
  const { kind, user, subject } = card
  const mine = kind === 'invite' && viewerId !== null && subject.id === viewerId
  const [declining, setDeclining] = useState(false)
  const [declined, setDeclined] = useState(false)
  const [failed, setFailed] = useState(false)
  const answer: StageCardAnswer = answered || (declined ? 'declined' : false)
  const open = mine && answer === false && Boolean(onJoin || onDecline)

  // The buttons go once the invite is answered. If one of them had focus, it moves to the line rather than the page.
  const lineRef = useRef<HTMLParagraphElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const focusInActions = useRef(false)
  useLayoutEffect(() => {
    if (open || !focusInActions.current) return
    focusInActions.current = false
    const active = document.activeElement
    if (!active || active === document.body) lineRef.current?.focus()
  }, [open])

  const decline = async () => {
    if (!onDecline || declining) return
    setFailed(false)
    const result = onDecline()
    if (!result || typeof (result as Promise<unknown>).then !== 'function') {
      setDeclined(true)
      return
    }
    setDeclining(true)
    try {
      await result
      setDeclined(true)
    } catch {
      setFailed(true)
    } finally {
      setDeclining(false)
    }
  }

  let line: ReactNode
  let pair: ReactNode
  if (kind === 'invite') {
    line = mine ? (
      <>
        <bdi className="watch-card__name">{nameOf(user)}</bdi> invited you on stage
      </>
    ) : (
      <>
        <bdi className="watch-card__name">{nameOf(user)}</bdi> invited <bdi className="watch-card__name">{nameOf(subject)}</bdi> on stage
      </>
    )
    pair = (
      <>
        <PersonAvatar person={user} />
        <PersonAvatar person={subject} />
      </>
    )
  } else {
    const Glyph = kind === 'joined' ? Mic : LogOut
    line = (
      <>
        <bdi className="watch-card__name">{nameOf(subject)}</bdi> {kind === 'joined' ? 'joined the stage' : 'left the stage'}
      </>
    )
    pair = (
      <>
        <PersonAvatar person={subject} />
        <span className="watch-card__glyph">
          <Glyph />
        </span>
      </>
    )
  }

  return (
    <Row
      className={cn('watch-card', className)}
      data-kind={kind}
      data-mine={mine || undefined}
      // Settled (joined, declined, or nothing to say): yours reads as everyone else's, the yellow being for an invite
      // still open or on its way.
      data-answered={(mine && answer !== false && answer !== 'waiting') || undefined}
    >
      <span className="watch-card__pair" aria-hidden="true">
        {pair}
      </span>
      <p ref={lineRef} className="watch-card__line" tabIndex={-1}>
        {line}
      </p>
      {open ? (
        <div
          ref={actionsRef}
          className="watch-card__actions"
          onFocus={() => {
            focusInActions.current = true
          }}
          onBlur={() => {
            // Moved on by the viewer (the camera check took it, say): forget it. Removed with the buttons: keep it.
            queueMicrotask(() => {
              const actions = actionsRef.current
              if (actions && !actions.contains(document.activeElement)) focusInActions.current = false
            })
          }}
        >
          {onJoin && (
            <Button size="compact" disabled={declining} onClick={onJoin}>
              Join stage
            </Button>
          )}
          {onDecline && (
            <Button variant="ghost" size="compact" loading={declining} onClick={() => void decline()}>
              Not now
            </Button>
          )}
          {note && (
            <span className="watch-card__state" data-state="note">
              <Radio aria-hidden="true" />
              {note}
            </span>
          )}
        </div>
      ) : (
        <AnswerState answer={answer} mine={mine} />
      )}
      {failed && open && (
        <p className="watch-card__failed" role="alert">
          <CircleAlert aria-hidden="true" />
          We couldn’t send your answer. Try again.
        </p>
      )}
    </Row>
  )
}

function AnswerState({ answer, mine }: { answer: StageCardAnswer; mine: boolean }) {
  if (answer === 'waiting') {
    return (
      <span className="watch-card__state" data-state="waiting">
        <LoaderCircle aria-hidden="true" />
        {mine ? 'Joining…' : 'Waiting'}
      </span>
    )
  }
  if (answer === 'joined') {
    return (
      <span className="watch-card__state" data-state="joined">
        <Check aria-hidden="true" />
        Joined
      </span>
    )
  }
  if (answer === 'declined') {
    return (
      <span className="watch-card__state" data-state="declined">
        Declined
      </span>
    )
  }
  return null
}
